(() => {
  'use strict';

  if (window.envelopePurchaseLimitInitialized) return;
  window.envelopePurchaseLimitInitialized = true;

  const FALLBACK_MESSAGE = 'You can only buy one envelope of each product.';
  const CORRECTED_KEY = 'envelope-limit-notice';
  const INTERNAL_HEADER = 'X-Envelope-Limit-Internal';

  const configNode = document.getElementById('EnvelopePurchaseLimitConfig');
  let config = {
    message: FALLBACK_MESSAGE,
    purchasedProductIds: []
  };

  if (configNode) {
    try {
      config = {
        ...config,
        ...JSON.parse(configNode.textContent || '{}')
      };
    } catch (error) {
      console.error('Envelope limit config could not be read.', error);
    }
  }

  const MESSAGE = config.message || FALLBACK_MESSAGE;
  const purchasedProductIds = new Set(
    (config.purchasedProductIds || []).map((id) => String(id))
  );
  const envelopeVariants = new Map();

  let cartCache = null;
  let cartCacheAt = 0;
  let noticeTimer = 0;
  let syncTimer = 0;
  let correctingCart = false;

  const originalFetch = window.fetch.bind(window);
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  const rootUrl = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const isOneEnvelopeTitle = (title) =>
    String(title || '')
      .split('/')
      .map((part) => part.trim().toLowerCase().replace(/\s+/g, ' '))
      .some((part) => part === '1 envelope' || part === '1 envolope');

  const rememberVariant = (variantId, productId) => {
    if (!variantId || !productId) return;
    envelopeVariants.set(String(variantId), String(productId));
  };

  const elements = (scope, selector) => {
    if (!scope || scope === document || scope.nodeType === 9) {
      return Array.from(document.querySelectorAll(selector));
    }

    if (scope.nodeType !== 1) return [];

    const matches = Array.from(scope.querySelectorAll(selector));
    if (scope.matches(selector)) matches.unshift(scope);
    return matches;
  };

  const scan = (scope = document) => {
    elements(scope, '[data-one-envelope-variant], [data-variant-title]').forEach((element) => {
      const title = element.getAttribute('data-variant-title');
      const marked = element.hasAttribute('data-one-envelope-variant');

      if (!marked && !isOneEnvelopeTitle(title)) return;

      const variantId =
        element.getAttribute('data-one-envelope-variant') ||
        element.getAttribute('data-variant-id') ||
        element.value;

      const productId =
        element.getAttribute('data-one-envelope-product') ||
        element.closest('[data-product-id]')?.getAttribute('data-product-id') ||
        element.closest('form')?.getAttribute('data-productid') ||
        element.closest('form')?.getAttribute('data-product-id');

      rememberVariant(variantId, productId);
    });

    elements(scope, '[data-full-product]').forEach((form) => {
      if (form.dataset.envelopeProductScanned === 'true') return;

      try {
        const product = JSON.parse(form.getAttribute('data-full-product') || '{}');
        (product.variants || []).forEach((variant) => {
          if (isOneEnvelopeTitle(variant.title) || isOneEnvelopeTitle(variant.public_title)) {
            rememberVariant(variant.id, product.id);
          }
        });
        form.dataset.envelopeProductScanned = 'true';
      } catch (error) {
        form.dataset.envelopeProductScanned = 'true';
      }
    });
  };

  const learnFromCart = (cart) => {
    (cart?.items || []).forEach((item) => {
      if (isOneEnvelopeTitle(item.variant_title)) {
        rememberVariant(item.variant_id, item.product_id);
      }
    });
  };

  const itemIsEnvelope = (item) =>
    Boolean(item) &&
    (envelopeVariants.has(String(item.variant_id)) || isOneEnvelopeTitle(item.variant_title));

  const envelopeQuantityForProduct = (cart, productId) =>
    (cart?.items || []).reduce((total, item) => {
      if (!itemIsEnvelope(item) || String(item.product_id) !== String(productId)) {
        return total;
      }

      return total + Number(item.quantity || 0);
    }, 0);

  const findCartItem = (cart, idOrKey) =>
    (cart?.items || []).find(
      (item) => String(item.key) === String(idOrKey) || String(item.variant_id) === String(idOrKey)
    );

  const showNotice = () => {
    const notice =
      document.querySelector('[data-envelope-limit-notice]') ||
      (() => {
        const element = document.createElement('div');
        element.className = 'envelope-limit-notice';
        element.setAttribute('data-envelope-limit-notice', '');
        element.setAttribute('role', 'status');
        element.setAttribute('aria-live', 'assertive');
        document.body.appendChild(element);
        return element;
      })();

    notice.textContent = MESSAGE;
    notice.setAttribute('aria-hidden', 'false');
    notice.classList.add('is-visible');
    window.clearTimeout(noticeTimer);
    noticeTimer = window.setTimeout(() => {
      notice.classList.remove('is-visible');
      notice.setAttribute('aria-hidden', 'true');
    }, 4200);
  };

  const limitMessageElement = (container) =>
    container?.querySelector(
      '[data-envelope-limit-message], .collection-form-message, [data-cross-sell-message], [data-minicart-recommendation-message]'
    );

  const showLimitMessage = (container) => {
    const element = limitMessageElement(container);
    if (!element) return;

    element.hidden = false;
    element.textContent = MESSAGE;
    element.classList.add('is-error');
    element.classList.remove('is-success');
  };

  const clearLimitMessage = (container) => {
    const element = limitMessageElement(container);
    if (!element || element.textContent !== MESSAGE) return;

    element.hidden = true;
    element.textContent = '';
    element.classList.remove('is-error');
  };

  const nativeFetch = (input, init = {}) => {
    const headers = new Headers(init.headers || {});
    headers.set(INTERNAL_HEADER, '1');
    return originalFetch(input, { ...init, headers });
  };

  const getCart = async (force = false) => {
    if (!force && cartCache && Date.now() - cartCacheAt < 1500) {
      return cartCache;
    }

    const response = await nativeFetch(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('The cart could not be read.');
    }

    cartCache = await response.json();
    cartCacheAt = Date.now();
    learnFromCart(cartCache);
    return cartCache;
  };

  const invalidateCart = () => {
    cartCache = null;
    cartCacheAt = 0;
  };

  const requestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && typeof input.url === 'string') return input.url;
    return '';
  };

  const isCartAdd = (url) => /\/cart\/add(\.js)?(\?|$)/.test(String(url));
  const isCartWrite = (url) => /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(String(url));

  const isInternalRequest = (init) => {
    const headers = init?.headers;
    if (!headers) return false;
    if (typeof Headers !== 'undefined' && headers instanceof Headers) {
      return headers.get(INTERNAL_HEADER) === '1';
    }
    return headers[INTERNAL_HEADER] === '1' || headers[INTERNAL_HEADER.toLowerCase()] === '1';
  };

  const parseBody = (body) => {
    if (!body) return null;

    if (typeof body === 'string') {
      const trimmed = body.trim();
      if (!trimmed) return null;

      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          return JSON.parse(trimmed);
        } catch (error) {
          return null;
        }
      }

      return Object.fromEntries(new URLSearchParams(trimmed));
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      return Object.fromEntries(body);
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const payload = {};
      body.forEach((value, key) => {
        payload[key] = value;
      });
      return payload;
    }

    return null;
  };

  const addAttempts = (payload) => {
    if (!payload || typeof payload !== 'object') return [];

    if (Array.isArray(payload.items)) {
      return payload.items
        .filter((item) => item && item.id)
        .map((item) => ({
          variantId: String(item.id),
          quantity: Math.max(1, Number(item.quantity || 1))
        }));
    }

    if (payload.id) {
      return [
        {
          variantId: String(payload.id),
          quantity: Math.max(1, Number(payload.quantity || 1))
        }
      ];
    }

    return [];
  };

  const addIsBlocked = (cart, attempt) => {
    const productId = envelopeVariants.get(String(attempt.variantId));
    if (!productId) return false;
    if (purchasedProductIds.has(productId)) return true;
    return envelopeQuantityForProduct(cart, productId) + Number(attempt.quantity || 1) > 1;
  };

  const clampUpdates = (cart, updates) => {
    let clamped = false;
    const seen = new Set();
    const next = { ...updates };

    Object.keys(next).forEach((key) => {
      const item = findCartItem(cart, key);
      if (!item || !itemIsEnvelope(item)) return;

      const productId = String(item.product_id);
      const quantity = Number(next[key]);

      if ((purchasedProductIds.has(productId) || seen.has(productId)) && quantity > 0) {
        next[key] = 0;
        clamped = true;
        return;
      }

      if (quantity > 1) {
        next[key] = 1;
        clamped = true;
      }

      if (Number(next[key]) > 0) seen.add(productId);
    });

    return { updates: next, clamped };
  };

  const enforcePayload = async (url, payload) => {
    scan();
    const cart = await getCart(true);

    if (isCartAdd(url)) {
      const blocked = addAttempts(payload).some((attempt) => addIsBlocked(cart, attempt));
      return { block: blocked, payload };
    }

    if (!payload || typeof payload !== 'object') {
      return { block: false, payload };
    }

    const next = { ...payload };
    let clamped = false;

    if (next.updates && !Array.isArray(next.updates)) {
      const result = clampUpdates(cart, next.updates);
      next.updates = result.updates;
      clamped = result.clamped;
    }

    if (Array.isArray(next.updates) && cart.items && next.updates.length === cart.items.length) {
      const seen = new Set();
      next.updates = next.updates.map((quantity, index) => {
        const item = cart.items[index];
        if (!item || !itemIsEnvelope(item)) return quantity;

        const productId = String(item.product_id);
        if (purchasedProductIds.has(productId) || seen.has(productId)) {
          clamped = true;
          return 0;
        }

        seen.add(productId);

        if (Number(quantity) > 1) {
          clamped = true;
          return 1;
        }

        return quantity;
      });
    }

    if (next.id != null && next.quantity != null && !Array.isArray(next.items)) {
      const item = findCartItem(cart, next.id);
      if (item && itemIsEnvelope(item)) {
        const productId = String(item.product_id);
        if (purchasedProductIds.has(productId) && Number(next.quantity) > 0) {
          next.quantity = 0;
          clamped = true;
        } else if (Number(next.quantity) > 1) {
          next.quantity = 1;
          clamped = true;
        }
      }
    }

    if (clamped) showNotice();
    return { block: false, payload: next, clamped };
  };

  const replacedBody = (originalBody, payload, clamped) => {
    if (!clamped) return null;
    if (typeof originalBody === 'string') return JSON.stringify(payload);
    if (typeof FormData !== 'undefined' && originalBody instanceof FormData) {
      const formData = new FormData();
      originalBody.forEach((value, key) => formData.append(key, value));
      if (payload.quantity != null) formData.set('quantity', String(payload.quantity));
      if (payload.id != null) formData.set('id', String(payload.id));
      return formData;
    }
    return JSON.stringify(payload);
  };

  const blockedResponse = () =>
    new Response(
      JSON.stringify({
        status: 422,
        message: MESSAGE,
        description: MESSAGE
      }),
      {
        status: 422,
        headers: { 'Content-Type': 'application/json' }
      }
    );

  window.fetch = async (input, init) => {
    if (isInternalRequest(init)) return originalFetch(input, init);

    const url = requestUrl(input);
    const method = String(init?.method || input?.method || 'GET').toUpperCase();

    if (!isCartWrite(url) || method === 'GET') {
      return originalFetch(input, init);
    }

    let body = init?.body;
    if (body == null && input instanceof Request) {
      try {
        body = await input.clone().text();
      } catch (error) {
        body = null;
      }
    }

    try {
      const result = await enforcePayload(url, parseBody(body));

      if (result.block) {
        showNotice();
        return blockedResponse();
      }

      const nextBody = replacedBody(body, result.payload, result.clamped);
      if (nextBody == null) return originalFetch(input, init);

      return originalFetch(requestUrl(input), {
        ...(init || {}),
        method,
        body: nextBody
      });
    } catch (error) {
      console.error('Envelope limit check failed.', error);
      return originalFetch(input, init);
    }
  };

  const rejectXhr = (xhr) => {
    const payload = JSON.stringify({
      status: 422,
      message: MESSAGE,
      description: MESSAGE
    });

    ['readyState', 'status', 'statusText', 'responseText', 'response'].forEach((key) => {
      const values = {
        readyState: 4,
        status: 422,
        statusText: 'Unprocessable Entity',
        responseText: payload,
        response: payload
      };

      try {
        Object.defineProperty(xhr, key, {
          configurable: true,
          get: () => values[key]
        });
      } catch (error) {
        // Native XHR fields stay in place. The notice still explains the limit.
      }
    });

    window.setTimeout(() => {
      xhr.dispatchEvent(new Event('readystatechange'));
      xhr.dispatchEvent(new ProgressEvent('load'));
      xhr.dispatchEvent(new ProgressEvent('loadend'));
      if (typeof xhr.onreadystatechange === 'function') xhr.onreadystatechange();
      if (typeof xhr.onload === 'function') xhr.onload();
    }, 0);
  };

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__envelopeMethod = method;
    this.__envelopeUrl = url;
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    const xhr = this;
    const url = String(xhr.__envelopeUrl || '');
    const method = String(xhr.__envelopeMethod || 'GET').toUpperCase();

    if (!isCartWrite(url) || method === 'GET') {
      return originalSend.call(xhr, body);
    }

    enforcePayload(url, parseBody(body))
      .then((result) => {
        if (result.block) {
          showNotice();
          rejectXhr(xhr);
          return;
        }

        const nextBody = replacedBody(body, result.payload, result.clamped);
        originalSend.call(xhr, nextBody == null ? body : nextBody);
      })
      .catch((error) => {
        console.error('Envelope limit check failed.', error);
        originalSend.call(xhr, body);
      });
  };

  const selectedVariantId = (container) => {
    const checkedCard = container.querySelector('[data-variant-card-input]:checked');
    if (checkedCard) return checkedCard.value;

    if (container.matches('[data-product-variant-card-form]')) {
      return container.querySelector('[data-selected-variant]')?.value || '';
    }

    const select = Array.from(container.querySelectorAll('select')).find(
      (element) =>
        !element.closest('.no-js') &&
        element.matches(
          'select[name="id"], .collection-variant-select, [data-cross-sell-variant], [data-minicart-recommendation-variant]'
        )
    );

    if (select) return select.value;

    return (
      container.querySelector('[data-selected-variant]')?.value ||
      container.querySelector('[name="id"]')?.value ||
      container.querySelector('[data-variant-id]')?.getAttribute('data-variant-id') ||
      container.querySelector('[data-minicart-recommendation-add]')?.getAttribute('data-variant-id') ||
      ''
    );
  };

  const selectedEnvelope = (container) => {
    if (!container) return null;
    scan(container);

    const variantId = selectedVariantId(container);
    if (!variantId || !envelopeVariants.has(String(variantId))) return null;

    return {
      variantId: String(variantId),
      productId: envelopeVariants.get(String(variantId))
    };
  };

  const containers = () =>
    Array.from(
      document.querySelectorAll(
        [
          '[data-product-variant-card-form]',
          '.js-collection-product-form',
          '[data-cart-recommendation-controls]',
          '[data-product-cross-sell-card]',
          '[data-minicart-recommendation-card]'
        ].join(',')
      )
    );

  const applyQuantityCap = (container, isEnvelope) => {
    const input = container.querySelector('input[name="quantity"], .collection-quantity-input');
    if (!input) return;

    if (isEnvelope) {
      if (input.dataset.envelopeMaxApplied !== 'true') {
        input.dataset.envelopePreviousMax = input.hasAttribute('max') ? input.getAttribute('max') : '';
        input.dataset.envelopeMaxApplied = 'true';
      }

      input.max = '1';
      if (Number(input.value) > 1) input.value = '1';
      return;
    }

    if (input.dataset.envelopeMaxApplied === 'true') {
      if (input.dataset.envelopePreviousMax) {
        input.max = input.dataset.envelopePreviousMax;
      } else {
        input.removeAttribute('max');
      }

      delete input.dataset.envelopeMaxApplied;
      delete input.dataset.envelopePreviousMax;
    }
  };

  const syncContainer = (container, cart) => {
    const selected = selectedEnvelope(container);
    applyQuantityCap(container, Boolean(selected));

    const button = container.querySelector(
      '[data-submit-button], .btn-add-to-cart, .collection-add-to-cart, [data-cart-recommendation-add], [data-cross-sell-add], [data-minicart-recommendation-add]'
    );

    if (!selected) {
      clearLimitMessage(container);
      if (button?.dataset.envelopeLocked === 'true') {
        button.disabled = false;
        delete button.dataset.envelopeLocked;
      }
      return;
    }

    const alreadyPurchased = purchasedProductIds.has(selected.productId);
    const alreadyInCart = cart ? envelopeQuantityForProduct(cart, selected.productId) > 0 : false;
    const blocked = alreadyPurchased || alreadyInCart;

    if (!blocked) {
      clearLimitMessage(container);
      if (button?.dataset.envelopeLocked === 'true') {
        const unavailable =
          container.querySelector('[data-variant-card-input]:checked')?.dataset.variantAvailable === 'false' ||
          container.querySelector('select')?.selectedOptions?.[0]?.disabled;
        button.disabled = Boolean(unavailable);
        delete button.dataset.envelopeLocked;
      }
      return;
    }

    showLimitMessage(container);
    if (button) {
      button.disabled = true;
      button.dataset.envelopeLocked = 'true';
    }

    if (alreadyPurchased && container.dataset.envelopePurchaseNotified !== selected.variantId) {
      container.dataset.envelopePurchaseNotified = selected.variantId;
      showNotice();
    }
  };

  const syncSurfaces = async () => {
    scan();
    let cart = null;

    try {
      cart = await getCart();
    } catch (error) {
      cart = null;
    }

    containers().forEach((container) => syncContainer(container, cart));
  };

  const scheduleSync = () => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncSurfaces, 40);
  };

  const readFormAttempt = (form) => {
    scan(form);
    const selected = selectedEnvelope(form);
    if (!selected) return null;

    const quantityInput = form.querySelector('input[name="quantity"]');
    return {
      ...selected,
      quantity: Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1)
    };
  };

  const isAddForm = (form) => {
    const action = form.getAttribute('action') || '';
    return action.includes('/cart/add') || form.matches('[data-product-form], .js-collection-product-form');
  };

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !isAddForm(form)) return;

      if (form.dataset.envelopeBypass === '1') {
        delete form.dataset.envelopeBypass;
        return;
      }

      const attempt = readFormAttempt(form);
      if (!attempt) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      getCart(true)
        .then((cart) => {
          const blocked =
            purchasedProductIds.has(attempt.productId) ||
            envelopeQuantityForProduct(cart, attempt.productId) + attempt.quantity > 1;

          if (blocked) {
            showNotice();
            showLimitMessage(form);
            const button = form.querySelector('[type="submit"], .btn-add-to-cart, .collection-add-to-cart');
            if (button) {
              button.disabled = true;
              button.dataset.envelopeLocked = 'true';
            }
            return;
          }

          form.dataset.envelopeBypass = '1';
          const submitter = event.submitter || form.querySelector('[type="submit"]');
          if (typeof form.requestSubmit === 'function') {
            if (submitter) form.requestSubmit(submitter);
            else form.requestSubmit();
          } else {
            form.submit();
          }
        })
        .catch((error) => {
          console.error('Envelope limit check failed.', error);
          form.dataset.envelopeBypass = '1';
          if (typeof form.requestSubmit === 'function') form.requestSubmit();
        });
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest(
        '[data-minicart-recommendation-add], [data-cross-sell-add], [data-cart-recommendation-add]'
      );

      if (!button) return;

      if (button.dataset.envelopeBypass === '1') {
        delete button.dataset.envelopeBypass;
        return;
      }

      const container = button.closest(
        '[data-cart-recommendation-controls], [data-product-cross-sell-card], [data-minicart-recommendation-card]'
      );
      const selected = selectedEnvelope(container);
      if (!selected) return;

      const quantityInput = container.querySelector('input[name="quantity"], .collection-quantity-input');
      const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      getCart(true)
        .then((cart) => {
          const blocked =
            purchasedProductIds.has(selected.productId) ||
            envelopeQuantityForProduct(cart, selected.productId) + quantity > 1;

          if (blocked) {
            showNotice();
            showLimitMessage(container);
            button.disabled = true;
            button.dataset.envelopeLocked = 'true';
            return;
          }

          button.dataset.envelopeBypass = '1';
          button.click();
        })
        .catch(() => {
          button.dataset.envelopeBypass = '1';
          button.click();
        });
    },
    true
  );

  const quantityInputFor = (control) => {
    const wrapper = control.closest(
      '.quantity-input-wrapper, .quantity-wrapper, .collection-quantity-selector, [data-custom-quantity-input-inside]'
    );

    return (
      wrapper?.querySelector('input[name="quantity"], input[name="updates[]"], .collection-quantity-input') ||
      control.parentElement?.querySelector('input')
    );
  };

  const inputIsEnvelope = (input) => {
    if (!input) return false;
    if (input.hasAttribute('data-one-envelope-variant')) return true;

    const container = input.closest(
      'form, [data-cart-recommendation-controls], [data-product-cross-sell-card], [data-minicart-recommendation-card], .cart-product-row'
    );

    return Boolean(selectedEnvelope(container));
  };

  document.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest(
        '.quantity-input-control--plus, [data-custom-quantity-increment], .collection-quantity-arrow[data-quantity-change="1"]'
      );

      if (!control) return;

      const input = quantityInputFor(control);
      if (!inputIsEnvelope(input)) return;
      if ((Number.parseInt(input.value, 10) || 0) < 1) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      showNotice();
    },
    true
  );

  document.addEventListener(
    'input',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !inputIsEnvelope(input)) return;
      if ((Number.parseInt(input.value, 10) || 0) <= 1) return;

      input.value = '1';
      showNotice();
    },
    true
  );

  const updateCartCount = (count) => {
    if (typeof count !== 'number') return;

    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count < 1;
    });
  };

  const correctCart = async () => {
    if (correctingCart) return;
    correctingCart = true;

    try {
      const cart = await getCart(true);
      const updates = {};
      const seen = new Set();
      let changed = false;

      (cart.items || []).forEach((item) => {
        if (!itemIsEnvelope(item)) return;

        const productId = String(item.product_id);
        if (purchasedProductIds.has(productId) || seen.has(productId)) {
          if (item.quantity > 0) {
            updates[item.key] = 0;
            changed = true;
          }
          return;
        }

        if (item.quantity > 1) {
          updates[item.key] = 1;
          changed = true;
        }

        seen.add(productId);
      });

      let pendingNotice = false;
      try {
        pendingNotice = sessionStorage.getItem(CORRECTED_KEY) === '1';
        if (!changed) sessionStorage.removeItem(CORRECTED_KEY);
      } catch (error) {
        pendingNotice = false;
      }

      if (!changed) {
        if (pendingNotice) showNotice();
        return;
      }

      await nativeFetch(`${rootUrl()}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ updates })
      });

      invalidateCart();
      showNotice();
      scheduleSync();

      if (document.querySelector('[data-ajax-main-cart]')) {
        try {
          sessionStorage.setItem(CORRECTED_KEY, '1');
        } catch (error) {
          // The notice was already shown.
        }
        window.location.reload();
        return;
      }

      const updated = await getCart(true);
      updateCartCount(updated.item_count);
    } catch (error) {
      console.error('Envelope cart correction failed.', error);
    } finally {
      correctingCart = false;
    }
  };

  document.addEventListener('cart:updated', () => {
    invalidateCart();
    scheduleSync();
  });

  document.addEventListener('change', (event) => {
    if (
      event.target.closest(
        '[data-variant-card-input], select[name="id"], .collection-variant-select, [data-cross-sell-variant], [data-minicart-recommendation-variant]'
      )
    ) {
      scheduleSync();
    }
  });

  document.addEventListener('product:variant-change', scheduleSync);

  if (document.body) {
    const relevantSelector = [
      '[data-product-variant-card-form]',
      '.js-collection-product-form',
      '[data-cart-recommendation-controls]',
      '[data-product-cross-sell-card]',
      '[data-minicart-recommendation-card]',
      '[data-one-envelope-variant]',
      '[data-variant-title]',
      '[data-full-product]'
    ].join(',');

    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.(relevantSelector) || node.querySelector?.(relevantSelector))
        )
      );

      if (!relevant) return;
      scan();
      scheduleSync();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  scan();
  scheduleSync();
  correctCart();
})();
